import isEqual from 'lodash-es/isEqual'
import {createContext, memo, use, useCallback, useMemo, useState} from 'react'

import type {TableHierarchySettings} from '../../../api/view/contracts'
import {DefaultSearchConfig} from '../../../components/filter-bar/search-context'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useVerticalGroupedBy} from '../../../features/grouping/hooks/use-vertical-grouped-by'
import {useSliceBy} from '../../../features/slicing/hooks/use-slice-by'
import {ViewType} from '../../../helpers/view-type'
import {usePaginatedVariablesWithFieldIds} from '../../../hooks/use-paginated-variables-with-field-ids'
import {useSortedBy} from '../../../hooks/use-sorted-by'
import {useViews} from '../../../hooks/use-views'
import type {PaginatedMemexItemsQueryVariables} from '../queries/types'
import {useNextPlaceholderQuery} from '../queries/use-next-placeholder-query'

export type StaleVariable = keyof PaginatedMemexItemsQueryVariables | undefined
type PaginatedViewVariablesContextType = {
  variables: PaginatedMemexItemsQueryVariables
  staleVariable: StaleVariable
  clearStaleVariable: () => void
}

const PaginatedViewVariablesContext = createContext<PaginatedViewVariablesContextType | null>(null)

export const PaginatedViewVariablesProvider = memo(function PaginatedViewVariablesProviderComponent({
  children,
}: {
  children: React.ReactNode
}) {
  const nextVariables = usePaginatedViewQueryVariables()
  const currentViewId = useViews().currentView?.id ?? -1

  const [currentContextByViewId, setCurrentContextByViewId] = useState<
    Record<number, Pick<PaginatedViewVariablesContextType, 'variables' | 'staleVariable'>>
  >(() => ({[currentViewId]: {variables: nextVariables, staleVariable: undefined}}))

  const {setUpNextPlaceholderQueries} = useNextPlaceholderQuery(nextVariables)

  const currentVariables = currentContextByViewId[currentViewId]?.variables
  if (!isEqual(currentVariables, nextVariables)) {
    // Optionally seed placeholder data to reduce layout shifts while fetching fresh data.
    // If no placeholder data is seeded, the user will see a loading screen every time
    // the variables for a view change.
    const staleVariable = showPlaceholderForStaleVariable(nextVariables, currentVariables)
    if (staleVariable) {
      setUpNextPlaceholderQueries(currentVariables)
      setCurrentContextByViewId(prev => ({
        ...prev,
        [currentViewId]: {variables: nextVariables, staleVariable},
      }))
    } else {
      if (showPlaceholderVerticalGroups(nextVariables, currentVariables)) {
        setUpNextPlaceholderQueries(currentVariables, true)
      }
      setCurrentContextByViewId(prev => {
        const prevContext = prev[currentViewId] ?? {staleVariable: undefined}
        return {
          ...prev,
          [currentViewId]: {...prevContext, variables: nextVariables},
        }
      })
    }
  }
  const clearStaleVariable = useCallback(() => {
    setCurrentContextByViewId(prev => {
      const prevContext = prev[currentViewId] ?? {variables: nextVariables}
      return {
        ...prev,
        [currentViewId]: {...prevContext, staleVariable: undefined},
      }
    })
  }, [currentViewId, nextVariables])
  const variables = currentVariables ?? nextVariables
  const staleVariable = currentContextByViewId[currentViewId]?.staleVariable
  const value = useMemo(
    () => ({variables, staleVariable, clearStaleVariable}),
    [variables, staleVariable, clearStaleVariable],
  )

  return <PaginatedViewVariablesContext value={value}>{children}</PaginatedViewVariablesContext>
})

export const usePaginatedViewVariablesContext = () => use(PaginatedViewVariablesContext)

/**
 *
 * Compares the next variables with the current variables to determine if we should seed placeholder
 * data to display while fresh data is fetched.
 *
 * @param nextVariables
 * @param currentVariables
 * @returns The variable that is stale, or undefined if none are stale.
 */
const showPlaceholderForStaleVariable = (
  nextVariables: PaginatedMemexItemsQueryVariables,
  currentVariables?: PaginatedMemexItemsQueryVariables,
): StaleVariable => {
  if (!currentVariables) return undefined
  if (!isEqual(nextVariables.viewType, currentVariables.viewType)) return undefined

  if (!isEqual(nextVariables.fieldIds, currentVariables.fieldIds)) return 'fieldIds'
  if (!isEqual(nextVariables.hierarchy, currentVariables.hierarchy)) return 'hierarchy'
  if (!isEqual(nextVariables.sumFields, currentVariables.sumFields)) return 'sumFields'
  if (!isEqual(nextVariables.q, currentVariables.q)) return 'q'
  if (!isEqual(nextVariables.sliceByColumnId, currentVariables.sliceByColumnId)) return 'sliceByColumnId'
  if (!isEqual(nextVariables.sliceByValue, currentVariables.sliceByValue)) return 'sliceByValue'
  if (!isEqual(nextVariables.sortedBy, currentVariables.sortedBy)) return 'sortedBy'

  return undefined
}

const showPlaceholderVerticalGroups = (
  nextVariables: PaginatedMemexItemsQueryVariables,
  currentVariables?: PaginatedMemexItemsQueryVariables,
): boolean => {
  const verticalGroupedByColumnIdUnchanged =
    nextVariables.verticalGroupedByColumnId &&
    isEqual(nextVariables.verticalGroupedByColumnId, currentVariables?.verticalGroupedByColumnId)
  if (!verticalGroupedByColumnIdUnchanged) return false
  // viewType should always be the same if verticalGroupedByColumnId is the same,
  // since verticalGroupedByColumnId should only be defined in board views, but we double check here
  const viewTypeUnchanged = isEqual(nextVariables.viewType, currentVariables?.viewType)
  return viewTypeUnchanged
}

function usePaginatedViewQueryVariables(): PaginatedMemexItemsQueryVariables {
  const {currentView} = useViews()
  const q = DefaultSearchConfig.getSearchQueryFromView(currentView)
  const {viewType, aggregationSettings, layoutSettings} = currentView?.localViewStateDeserialized ?? {}
  const {sorts} = useSortedBy()
  const sortedBy = useMemo(
    () => sorts.map(sort => ({columnId: sort.column.databaseId, direction: sort.direction})),
    [sorts],
  )
  const {groupedByColumnId: horizontalGroupedByColumnId} = useHorizontalGroupedBy()
  let {groupedByColumnId: verticalGroupedByColumnId} = useVerticalGroupedBy()

  // only board views use vertical grouping, but it's always defaulted for any view type
  if (viewType !== ViewType.Board) {
    verticalGroupedByColumnId = undefined
  }

  const {sliceField, sliceValue} = useSliceBy()
  const sliceByColumnId = sliceField?.id

  const sumFields = useMemo(() => {
    return aggregationSettings?.sum.map(f => f.id)
  }, [aggregationSettings?.sum])

  let hierarchy: TableHierarchySettings | undefined
  if (viewType === ViewType.Table) {
    hierarchy = layoutSettings?.table?.hierarchy
  }

  const variablesWithoutFieldIds: PaginatedMemexItemsQueryVariables = useMemo(
    () => ({
      q,
      sortedBy,
      viewType,
      horizontalGroupedByColumnId,
      verticalGroupedByColumnId,
      sliceByColumnId,
      // sliceValue can be `null`, which we want to coerce to undefined
      sliceByValue: sliceValue ?? undefined,
      sumFields,
      hierarchy,
    }),
    [
      q,
      sortedBy,
      viewType,
      horizontalGroupedByColumnId,
      verticalGroupedByColumnId,
      sliceByColumnId,
      sliceValue,
      sumFields,
      hierarchy,
    ],
  )

  return usePaginatedVariablesWithFieldIds(variablesWithoutFieldIds)
}
